/**
 * Component registry.
 *
 * Each component is a plain-data interface + a ComponentType<T> brand.
 * Add a new component by defining both here (or in a sibling file) and
 * re-exporting. Systems import the brand and query the world.
 */

import type { Graphics, Container } from 'pixi.js';
import { defineComponent } from '@/core/ECS';

// --- geometry ---

export interface PositionC { x: number; y: number; }
export const Position = defineComponent<PositionC>('position');

export interface VelocityC { vx: number; vy: number; }
export const Velocity = defineComponent<VelocityC>('velocity');

export interface DriftC {
  baseVx: number; baseVy: number;
  wanderSeed: number;
  strength: number;
}
export const Drift = defineComponent<DriftC>('drift');

// --- rendering ---

export interface RenderableC {
  root: Container;
  body: Graphics;
  glow?: Graphics;
  baseScale?: number;
  /** dynamic visual offset from breathing / squash-stretch */
  dynamicScaleX?: number;
  dynamicScaleY?: number;
  /** seconds-accumulator used by animated draws */
  time: number;
  /** called each frame to redraw the body; optional for static entities */
  draw?: (time: number, body: Graphics, glow?: Graphics) => void;
}
export const Renderable = defineComponent<RenderableC>('renderable');

// --- gameplay: food ---

export interface AbsorbableC {
  foodId: string;
  biomass: number;
  dna: number;
  radius: number;
  codexId?: string;
  /** Bloom membership — food spawned in a shape shares a bloomId; when all
   *  members are absorbed the bloom is considered "cleared" (bonus + cue). */
  bloomId?: number;
}
export const Absorbable = defineComponent<AbsorbableC>('absorbable');

// --- gameplay: predator ---

export interface HostileC {
  predatorId: string;
  damage: number;
  radius: number;
  speed: number;
  behavior: 'chase' | 'ambush' | 'patrol';
  hp: number;
  maxHp: number;
  attackCooldown: number;
  attackTimer: number;
  /** ambush predators: how long they pause before striking again */
  ambushTimer?: number;
  codexId?: string;
  /** AI phase — feeds MovementSystem's per-predator behavior tree */
  aiPhase?: 'idle' | 'windup' | 'strike' | 'recover';
  /** countdown for the current phase */
  phaseTimer?: number;
  /** -1 or +1 — which side of the player to flank toward (chase AI) */
  flankBias?: number;
  /** seconds until flankBias flips */
  flankSwapTimer?: number;
  /** seconds until the next "surprise" strike is allowed (chase AI) */
  surpriseCooldown?: number;
  /** current visual scale multiplier, tweened per phase for grow/shrink tell */
  visualScale?: number;
  /** per-predator angular offset used to scatter the pack around the player
   *  (radians). Assigned on first tick so multiple predators don't clump. */
  orbitAngle?: number;
  /** per-predator orbit ring radius — varies the approach line so the pack
   *  doesn't all sit on the same circle around the player. */
  orbitR?: number;
  /** seconds to lead-aim the player on strikes (uses player velocity). */
  leadFactor?: number;
  /** speed multiplier applied to the *current* strike. Resampled each strike
   *  so consecutive lunges don't have identical timing — the player can't
   *  pre-roll their dodge. */
  strikeMult?: number;
  /** tangential offset mixed into the current strike, in [-0.5, 0.5]. */
  strikeArc?: number;
  /** Bypasses player invulnerability — shield does nothing against this hit. */
  piercesShield?: boolean;
  /** On death, spawn N copies of `predatorId` at the death point. */
  splitsInto?: { predatorId: string; count: number; radiusMult?: number };
  /** Wander mode (player out of aggro range): current drift direction
   *  in radians. Re-rolled each time wanderTimer hits zero. */
  wanderAngle?: number;
  /** Seconds remaining before the wander direction re-rolls. */
  wanderTimer?: number;
}
export const Hostile = defineComponent<HostileC>('hostile');

// --- gameplay: powerups (bomb, shield) ---

export interface PowerupC {
  kind: 'bomb' | 'shield';
  radius: number;
  /** time in seconds since spawn — powerups despawn if ignored too long */
  age: number;
  lifetime: number;
}
export const Powerup = defineComponent<PowerupC>('powerup');

// --- gameplay: player-controlled cell ---

export interface PlayerCellC {
  radius: number;
  targetX: number; targetY: number;
  /** breathing phase */
  phase: number;
  trail: { x: number; y: number; age: number }[];
  absorptionRadius: number;
  /** pulse (right-click hold) state — keeps the halo visible while held */
  pulseRemaining: number;
  /** Whether the absorption halo is active. False when right-mouse is
   *  released — the cell can only absorb on direct contact, which is what
   *  enables the same-type food streak combo. */
  haloEnabled: boolean;
  /** dash (left-click) state */
  dashRemaining: number;
  invulnRemaining: number;
  dashVx: number; dashVy: number;
}
export const PlayerCell = defineComponent<PlayerCellC>('playerCell');

// --- floating text / particle / event banner helpers ---

export interface FloatingTextC {
  lifetime: number;
  age: number;
  text: string;
  vy: number;
  color: number;
}
export const FloatingText = defineComponent<FloatingTextC>('floatingText');

export interface ParticleC {
  age: number;
  lifetime: number;
  color: number;
  size: number;
}
export const Particle = defineComponent<ParticleC>('particle');

// --- tags ---

export interface TagC { name: string; }
export const Tag = defineComponent<TagC>('tag');
